import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { TripStopTerminationTypeEnum } from '../../types/trip-stop-termination-type';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientEntity } from './client.entity';
import { TripStopInspectionEntity } from './trip-stop-inspection.entity';
import { TripStopItemEntity } from './trip-stop-item.entity';
import { TripStopPhotoEntity } from './trip-stop-photo.entity';
import { TripEntity } from './trip.entity';

export type TripStopStatus =
	| 'pending'
	| 'arrived'
	| 'in-progress'
	| 'completed'
	| 'failed'
	| 'skipped';

/**
 * Discriminator for what kind of stop this row represents.
 *
 * - `client_delivery` — the default. A real delivery to a client at a
 *   client address. Every TripStop created prior to 2026-05-22 is this
 *   shape, and the column defaults to `'client_delivery'` so existing
 *   rows interpret correctly without a backfill.
 * - `warehouse_return` — a driver-initiated mid-trip return to the
 *   trip's `fkWarehouse` location. Added 2026-05-22 (defect #34) so the
 *   driver can signal "I need to swing by the warehouse to grab more
 *   inventory before resuming my pending stops." For rows with this
 *   stopType, `clientId` and `clientAddressId` are NULL — the warehouse
 *   identity comes from `trip.fkWarehouseId` (the wizard-level
 *   warehouse from defect #33).
 * - `pause` — a driver-initiated trip pause (Meal / Fuel / Break /
 *   Mechanical / Other). Added 2026-05-22 (defect B19) instead of a
 *   separate `trip_pause` table, because pauses share the same shape
 *   as stops (sequence, arrival/departure timestamps, status, audit).
 *   For rows with this stopType: `clientId` and `clientAddressId` are
 *   NULL, `pauseCategory` is set to the reason. Driver "resumes" by
 *   tapping the next real stop's Start action — the existing stop
 *   arrival flow completes the pause stop (sets departureTime + status
 *   = 'completed') before advancing to the next.
 */
export type TripStopType = 'client_delivery' | 'warehouse_return' | 'pause';

/**
 * One stop on a Trip — a specific delivery to a specific client address at a
 * specific position in the trip sequence. Replaces the legacy
 * `route_stop` + `stop_execution` pair (those two tables collapsed into
 * this one).
 *
 * Multiple Orders can attach to a single TripStop via `order.fk_trip_stop__id`
 * — one truck stop, potentially many orders being delivered (combined
 * delivery to the same client address).
 */
@Entity('trip_stop')
@Index('idx_trip_stop_trip', ['tripId'])
@Index('idx_trip_stop_client', ['clientId'])
export class TripStopEntity extends BaseEntity {
	@Column({ name: 'fk_trip__id', type: 'int', unsigned: true })
	public tripId!: number;

	/**
	 * NULL for `stopType = 'warehouse_return'` stops (defect #34, 2026-05-22).
	 * Required for `stopType = 'client_delivery'`. A migration relaxed the
	 * column's NOT NULL constraint — see
	 * `1779700000000-MakeTripStopClientIdNullable.ts`.
	 */
	@Column({
		name: 'fk_client__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientId?: number | null;

	@Column({
		name: 'fk_client_address__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientAddressId?: number;

	@Column({ name: 'sequence', type: 'int' })
	public sequence!: number;

	@Column({ name: 'deliveryWindowStart', type: 'datetime', nullable: true })
	public deliveryWindowStart?: Date;

	@Column({ name: 'deliveryWindowEnd', type: 'datetime', nullable: true })
	public deliveryWindowEnd?: Date;

	@Column({ name: 'estimatedArrival', type: 'datetime', nullable: true })
	public estimatedArrival?: Date;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes?: string;

	@Column({ name: 'specialInstructions', type: 'text', nullable: true })
	public specialInstructions?: string;

	@Column({
		name: 'status',
		type: 'varchar',
		length: 50,
		default: "'pending'",
	})
	public status!: TripStopStatus;

	/**
	 * Defect #34 (2026-05-22): discriminates client deliveries from
	 * driver-initiated warehouse-return stops. Existing rows default
	 * to `'client_delivery'` so the additive sync is non-destructive.
	 */
	@Column({
		name: 'stopType',
		type: 'varchar',
		length: 40,
		default: "'client_delivery'",
	})
	public stopType!: TripStopType;

	@Column({
		name: 'arrivalLatitude',
		type: 'decimal',
		precision: 10,
		scale: 8,
		nullable: true,
	})
	public arrivalLatitude?: number | null;

	@Column({
		name: 'arrivalLongitude',
		type: 'decimal',
		precision: 11,
		scale: 8,
		nullable: true,
	})
	public arrivalLongitude?: number | null;

	@Column({ name: 'gpsValidated', type: 'boolean', default: false })
	public gpsValidated!: boolean;

	@Column({ name: 'gpsDistanceMeters', type: 'int', nullable: true })
	public gpsDistanceMeters?: number | null;

	@Column({ name: 'arrivalTime', type: 'datetime', nullable: true })
	public arrivalTime?: Date | null;

	@Column({ name: 'departureTime', type: 'datetime', nullable: true })
	public departureTime?: Date | null;

	/**
	 * Task #56 (Departure GPS) — captured when the driver hits "Complete
	 * Stop". Mirrors the arrival GPS columns but stamps the position
	 * AT departure so an auditor can:
	 *  - Prove the driver physically moved on from the stop (anti-fraud:
	 *    no completing N stops from a parking lot).
	 *  - Compute per-stop dwell distance (departure - arrival) for
	 *    drive-time analytics.
	 * Null when the driver's device doesn't grant location at completion
	 * time — we don't block stop completion on a missing fix, just record
	 * what we get.
	 */
	@Column({
		name: 'departureLatitude',
		type: 'decimal',
		precision: 10,
		scale: 8,
		nullable: true,
	})
	public departureLatitude?: number | null;

	@Column({
		name: 'departureLongitude',
		type: 'decimal',
		precision: 11,
		scale: 8,
		nullable: true,
	})
	public departureLongitude?: number | null;

	@Column({ name: 'signatureData', type: 'text', nullable: true })
	public signatureData?: string | null;

	/**
	 * Set to `true` when the driver explicitly tapped the "Skip
	 * signature" toggle at the stop (e.g. recipient was unavailable
	 * / refused to sign / locked door). Distinct from
	 * `signatureData IS NULL` because a NULL there could mean
	 * "not yet captured" mid-flow; this flag means "intentionally
	 * skipped, complete the stop without a signature". The driver
	 * can untoggle and capture later if the recipient turns up.
	 */
	@Column({ name: 'signatureSkipped', type: 'boolean', default: false })
	public signatureSkipped!: boolean;

	@Column({
		name: 'signerName',
		type: 'varchar',
		length: 255,
		nullable: true,
	})
	public signerName?: string | null;

	@Column({ name: 'signatureTimestamp', type: 'datetime', nullable: true })
	public signatureTimestamp?: Date | null;

	@Column({
		name: 'signatureLatitude',
		type: 'decimal',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public signatureLatitude?: number | null;

	@Column({
		name: 'signatureLongitude',
		type: 'decimal',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public signatureLongitude?: number | null;

	@Column({ name: 'driverNotes', type: 'text', nullable: true })
	public driverNotes?: string | null;

	@Column({ name: 'customerRefused', type: 'boolean', default: false })
	public customerRefused!: boolean;

	/**
	 * F-4 (2026-05-24) — free-form reason text explaining why this stop
	 * ended short of a successful delivery. Previously named
	 * `refusalReason` but the same column was overloaded for both
	 * customer-refusal text ("Customer refused delivery") and driver-
	 * initiated skip text ("Wrong / undeliverable address"). The
	 * accompanying `terminationType` enum below partitions these populations
	 * so reports/filters don't have to string-match the reason label.
	 *
	 * Text source: for skip-type terminations, the value is one of the
	 * admin-managed labels from
	 * `DistributionConfigPreferences.skipStopReasons`; for refusals /
	 * failures the driver enters free text.
	 */
	@Column({ name: 'terminationReason', type: 'text', nullable: true })
	public terminationReason?: string | null;

	/**
	 * F-4 (2026-05-24) — discriminator for WHY the stop terminated.
	 * Distinct from {@link terminationReason} (which carries the
	 * human-readable label).
	 *
	 * Set by the backend services:
	 *  - `skipStop` sets `'skip'`
	 *  - `failStop` with `customerRefused = true` sets `'refusal'`
	 *  - `failStop` with `customerRefused = false` sets `'failure'`
	 *
	 * Null until one of those endpoints fires, and null on every stop
	 * that completed successfully.
	 */
	@Column({
		name: 'terminationType',
		type: 'enum',
		enum: TripStopTerminationTypeEnum,
		nullable: true,
	})
	public terminationType?: TripStopTerminationTypeEnum | null;

	/**
	 * Pause-reason discriminator for `stopType = 'pause'` rows (B19,
	 * added 2026-05-22). Matches one of the `pauseCategories` codes
	 * configured under `DistributionConfigPreferences` — default seed
	 * is Meal / Fuel / Break / Mechanical / Other. Null for all other
	 * stopTypes (client_delivery + warehouse_return).
	 */
	@Column({
		name: 'pauseCategory',
		type: 'varchar',
		length: 40,
		nullable: true,
	})
	public pauseCategory?: string | null;

	/**
	 * On-behalf-of audit (B5, added 2026-05-22). When a warehouse
	 * manager completes a stop on the driver's behalf — e.g. the driver
	 * forgot to mark stop #3 as arrived and the WM cleaned it up — the
	 * driver's user ID lives here. Null when the driver did it
	 * themselves. The actual user is captured implicitly via
	 * BaseEntity's modifiedUserId.
	 */
	@Column({
		name: 'fk_user__id_on_behalf_of',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public onBehalfOfUserId?: number | null;

	@ManyToOne(() => TripEntity, (trip) => trip.tripStops)
	@JoinColumn({ name: 'fk_trip__id' })
	public fkTrip!: TripEntity;

	@ManyToOne(() => ClientEntity, { nullable: true })
	@JoinColumn({ name: 'fk_client__id' })
	public fkClient?: ClientEntity;

	@ManyToOne(() => ClientAddressEntity, { nullable: true })
	@JoinColumn({ name: 'fk_client_address__id' })
	public fkClientAddress?: ClientAddressEntity;

	@OneToMany(() => TripStopItemEntity, (item) => item.fkTripStop)
	public items?: Array<TripStopItemEntity>;

	@OneToMany(() => TripStopPhotoEntity, (photo) => photo.fkTripStop)
	public photos?: Array<TripStopPhotoEntity>;

	@OneToMany(
		() => TripStopInspectionEntity,
		(inspection) => inspection.fkTripStop,
	)
	public inspections?: Array<TripStopInspectionEntity>;
}
